import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import DarkTheme from "@mui/icons-material/Brightness2";
import LightTheme from "@mui/icons-material/Brightness7";
import SystemTheme from "@mui/icons-material/Contrast";
import LogoutIcon from "@mui/icons-material/Logout";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { drawerWidth, headerHeight } from "./constants";

import { ConfirmationDialog } from "@/components/dialog";
import type { User } from "@/models";

export type UserHeaderProps = {
    user: User | null;
    theme: "system" | "light" | "dark";
    setTheme: (mode: "system" | "light" | "dark") => void;
    logout: () => Promise<void> | void;
};

export function UserHeader({ user, theme, setTheme, logout }: UserHeaderProps) {
    const { t } = useTranslation();
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const isSharedUser = user ? getIsSharedUser(user) : true;
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    function handleMenu(e: React.MouseEvent<HTMLElement>) {
        if (open) {
            closeMenu();
            return;
        }
        setAnchor(e.currentTarget as HTMLElement);
    }

    function closeMenu() {
        setAnchor(null);
    }

    function getIsSharedUser(user: User): boolean {
        return user?.last_name === "Shared Login";
    }

    function getName(user: User | null): string {
        if (!user) return "";
        return isSharedUser
            ? user?.first_name
            : [user?.first_name, user?.last_name].filter(Boolean).join(" ");
    }

    function getInitials(user: User | null): string {
        if (!user) return "";
        return isSharedUser
            ? user?.first_name?.charAt(0).toUpperCase()
            : [user?.first_name, user?.last_name]
                  .filter(Boolean)
                  .map((n) => n.charAt(0).toUpperCase())
                  .join("");
    }

    return (
        <>
            <Box
                onClick={handleMenu}
                role="button"
                tabIndex={0}
                aria-haspopup="menu"
                aria-expanded={open ? "true" : undefined}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleMenu(
                            e as unknown as React.MouseEvent<HTMLElement>,
                        );
                    }
                }}
                sx={{
                    pl: 2,
                    pr: 1.25,
                    height: headerHeight,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: "action.hover",
                    },
                }}
            >
                <Avatar
                    sx={{
                        width: 38,
                        height: 38,
                        mr: 1,
                        color: "white",
                        bgcolor: "primary.main",
                        fontSize: 17,
                    }}
                >
                    {getInitials(user) || "U"}
                </Avatar>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {getName(user)}
                    </Box>
                    {!isSharedUser ? (
                        <Box
                            sx={{
                                color: "text.secondary",
                                fontSize: 12,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {user?.organisation.name}
                        </Box>
                    ) : null}
                </Stack>
                <Box
                    aria-hidden
                    sx={{
                        ml: 0.25,
                        display: "inline-flex",
                        alignItems: "center",
                    }}
                >
                    <ArrowDropDown />
                </Box>
                <Menu
                    anchorEl={anchor}
                    open={open}
                    onClose={closeMenu}
                    keepMounted
                    disablePortal
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: drawerWidth,
                                ml: -2,
                                borderRadius: "0 0 10px 10px",
                            },
                        },
                    }}
                    onClick={closeMenu}
                >
                    <MenuItem
                        sx={{ px: 2.5 }}
                        onClick={() => {
                            const nextTheme =
                                theme === "system"
                                    ? "light"
                                    : theme === "light"
                                      ? "dark"
                                      : "system";
                            setTheme(nextTheme);
                            closeMenu();
                        }}
                    >
                        {theme === "system" ? (
                            <SystemTheme fontSize="small" sx={{ mr: 2 }} />
                        ) : theme === "light" ? (
                            <LightTheme fontSize="small" sx={{ mr: 2 }} />
                        ) : (
                            <DarkTheme fontSize="small" sx={{ mr: 2 }} />
                        )}
                        {t("nav.toggle-theme")}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        sx={{ px: 2.5 }}
                        onClick={() => {
                            setShowLogoutDialog(true);
                            closeMenu();
                        }}
                    >
                        <LogoutIcon fontSize="small" sx={{ mr: 2 }} />{" "}
                        {t("logout")}
                    </MenuItem>
                </Menu>
            </Box>
            <ConfirmationDialog
                open={showLogoutDialog}
                title={t("confirm-logout")}
                onConfirm={async () => {
                    await logout();
                    setShowLogoutDialog(false);
                }}
                onCancel={() => setShowLogoutDialog(false)}
                maxWidth="xs"
                confirmText={t("logout")}
                cancelText={t("cancel")}
                kind="alert"
            >
                {t("confirm-logout-message")}
            </ConfirmationDialog>
        </>
    );
}
